import React from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  alpha,
  useTheme,
  Chip,
} from "@mui/material";
import {
  TableBar,
  CalendarToday,
  RestaurantMenu,
  LocalOffer,
} from "@mui/icons-material";

/**
 * BillReceipt Component
 * Preview of the bill/receipt with order details
 */
const BillReceipt = ({
  table,
  cart,
  customerName,
  customerMobile,
  payments = [],
  subtotal,
  serviceCharge = 0,
  serviceChargeRate = 0,
  vatAmount = 0,
  vatRate = 0,
  taxId,
  discountType,
  discountValue,
  discountAmount,
  total,
  orderType = "dine-in",
  currency = "Rs.",
}) => {
  const theme = useTheme();
  const currentDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const paymentLabels = {
    none: "No Payment",
    cash: "Cash",
    card: "Card",
    loyalty: "Loyalty",
    split: "Split",
  };

  const getPaymentMethodKey = (payment) =>
    payment?.paymentMethod || payment?.method || "none";

  const getPaymentColor = (method) => {
    switch (method) {
      case "cash":
        return theme.palette.success;
      case "card":
        return theme.palette.info;
      case "loyalty":
        return theme.palette.warning;
      default:
        return { main: theme.palette.grey[600] };
    }
  };

  const getOrderTypeLabel = (type) => {
    switch (type) {
      case "dine-in":
        return "Dine-In";
      case "delivery":
        return "Delivery";
      default:
        return "Takeaway";
    }
  };

  const getOrderTypePalette = (type) => {
    if (type === "dine-in") return theme.palette.success;
    if (type === "delivery") return theme.palette.warning;
    return theme.palette.info;
  };

  const chipSx = (palette) => {
    const main =
      palette?.main ?? theme.palette.text.secondary;
    return {
      height: 24,
      fontWeight: 700,
      fontSize: "0.75rem",
      bgcolor: alpha(main, 0.1),
      color: main,
      border: `1.5px solid ${alpha(main, 0.28)}`,
      "& .MuiChip-icon": { color: main },
    };
  };

  const orderTypePalette = getOrderTypePalette(orderType);
  const displayPayments =
    payments.length > 0 ? payments : [{ method: "none" }];

  return (
    <Paper
      elevation={3}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "white",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {/* Receipt Header */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          borderBottom: `3px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1.25,
          }}
        >
          <CalendarToday
            sx={{ fontSize: 15, color: theme.palette.text.secondary }}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 500, fontSize: "0.8125rem" }}
          >
            {currentDate}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 0.75,
          }}
        >
          <Chip
            icon={<RestaurantMenu sx={{ fontSize: "14px !important" }} />}
            label={getOrderTypeLabel(orderType)}
            size="small"
            sx={chipSx(orderTypePalette)}
          />
          {table && (
            <Chip
              icon={<TableBar sx={{ fontSize: "14px !important" }} />}
              label={`Table #${table.tableNumber}`}
              size="small"
              sx={chipSx(theme.palette.primary)}
            />
          )}
          {displayPayments.map((payment, index) => {
            const methodKey = getPaymentMethodKey(payment);
            const color = getPaymentColor(methodKey);
            return (
              <Chip
                key={`${methodKey}-${index}`}
                label={
                  paymentLabels[methodKey] ||
                  (methodKey ? methodKey.replace(/-/g, " ") : "Payment")
                }
                size="small"
                sx={chipSx(color)}
              />
            );
          })}
        </Box>
      </Box>

      {/* Customer Info */}
      {/* {(customerName || customerMobile) && (
        <Box sx={{ p: 2.5, bgcolor: alpha(theme.palette.grey[100], 0.5) }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, mb: 1, color: "text.secondary" }}
          >
            CUSTOMER DETAILS
          </Typography>
          {customerName && (
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {customerName}
            </Typography>
          )}
          {customerMobile && (
            <Typography variant="body2" color="text.secondary">
              {customerMobile}
            </Typography>
          )}
        </Box>
      )} */}

      {/* Order Items */}
      <Box sx={{ flex: 1, overflow: "auto", p: 2.5 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, mb: 2, color: "text.secondary" }}
        >
          ORDER ITEMS
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {cart.map((item, index) => {
            const itemPrice = item.finalPrice || item.price;
            const totalPrice = itemPrice * item.quantity;

            return (
              <Box key={index}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 0.5,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          lineHeight: 1.3,
                        }}
                      >
                        {item.name}
                      </Typography>
                      {item.isOffer && (
                        <Chip
                          icon={<LocalOffer sx={{ fontSize: 10 }} />}
                          label="OFFER"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.6rem",
                            fontWeight: 700,
                            bgcolor: theme.palette.error.main,
                            color: "white",
                            "& .MuiChip-icon": {
                              color: "white",
                            },
                          }}
                        />
                      )}
                    </Box>

                    {item.selectedVariation && (
                      <Chip
                        label={item.selectedVariation.name}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.65rem",
                          fontWeight: 600,
                          mt: 0.5,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                        }}
                      />
                    )}

                    {item.selectedModifiers &&
                      item.selectedModifiers.length > 0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            color: "text.secondary",
                            fontSize: "0.7rem",
                            mt: 0.5,
                          }}
                        >
                          +{" "}
                          {item.selectedModifiers.map((m) => m.name).join(", ")}
                        </Typography>
                      )}
                  </Box>

                  <Box sx={{ textAlign: "right", ml: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                    >
                      {currency} {totalPrice.toFixed(2)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.7rem" }}
                    >
                      {item.quantity} × {currency} {itemPrice.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
                {index < cart.length - 1 && (
                  <Divider sx={{ mt: 1.5, opacity: 0.5 }} />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Receipt Footer - Totals */}
      <Box
        sx={{
          p: 2.5,
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          borderTop: `3px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Subtotal
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {currency} {subtotal.toFixed(2)}
            </Typography>
          </Box>

          {/* Service Charge */}
          {serviceCharge > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: "text.secondary" }}
              >
                Service Charge ({serviceChargeRate}%)
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "text.secondary" }}
              >
                {currency} {serviceCharge.toFixed(2)}
              </Typography>
            </Box>
          )}

          {/* VAT */}
          {vatAmount > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: "text.secondary" }}
              >
                VAT ({vatRate}%) {taxId && `- ${taxId}`}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "text.secondary" }}
              >
                {currency} {vatAmount.toFixed(2)}
              </Typography>
            </Box>
          )}

          {/* Show discount if applied */}
          {discountType !== "none" && discountAmount > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                bgcolor: alpha(theme.palette.warning.main, 0.1),
                p: 1,
                borderRadius: 1,
                border: `1px dashed ${alpha(theme.palette.warning.main, 0.3)}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, color: theme.palette.warning.dark }}
                >
                  Discount
                </Typography>
                <Chip
                  label={
                    discountType === "percentage"
                      ? `${discountValue}%`
                      : `${currency} ${discountValue}`
                  }
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.7rem",
                    fontWeight: 800,
                    bgcolor: theme.palette.warning.main,
                    color: "white",
                  }}
                />
              </Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 800, color: theme.palette.warning.dark }}
              >
                - {currency} {discountAmount.toFixed(2)}
              </Typography>
            </Box>
          )}

          <Divider />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Total
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
              }}
            >
              {currency} {total.toFixed(2)}
            </Typography>
          </Box>

          {/* {paymentMethod && (
            <>
              <Divider />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Payment Method
                </Typography>
                <Chip
                  label={paymentLabels[paymentMethod] || paymentMethod}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor:
                      paymentMethod === "cash"
                        ? alpha(theme.palette.success.main, 0.1)
                        : paymentMethod === "card"
                          ? alpha(theme.palette.info.main, 0.1)
                          : alpha(theme.palette.grey[500], 0.1),
                    color:
                      paymentMethod === "cash"
                        ? theme.palette.success.main
                        : paymentMethod === "card"
                          ? theme.palette.info.main
                          : theme.palette.grey[700],
                  }}
                />
              </Box>
            </>
          )} */}
        </Box>
      </Box>
    </Paper>
  );
};

export default BillReceipt;
